import React from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { FormControl, FormLabel, Typography } from '@mui/material';
import { SetAsMainImageCheckbox } from '@/editor/PageEditor/SetAsMainImageCheckbox';
import { WysiwygEditor } from '@/editor/PageEditor/EditorBlock/TextBlock/WysiwygEditor';
import { AddMediaObject } from '@/components/AddMediaObject';
import Styles from '@/assets/js/Styles';
import { useControlledFieldArray } from '@/hooks/useControlledFieldArray';
import { BannerBackgroundM24MediaModel, BannerBlock } from '@/declarations/models/blocks/BannerBlock';
import { Page } from '@/declarations/models/Page';
import { IconFinderButtonSmall } from '@/components/Finder/IconFInderButtonSmall';
import { LinkType } from '@/declarations/models/LinkType';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import Container from '../../../../components/Container';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import TextInput from '../../../../components/forms/TextInput';
import SwitchInput from '../../../../components/forms/SwitchInput';
import { TextBlock as TextBlockModel } from '../../../../declarations/models/blocks/TextBlock';
import Links from '../../../../components/Links/Links';
import { useEditorEventHandler, useEditorEventManager } from '../../../lib/eventManager/EditorEventManager';
import TitleAlignmentSelector from '../CommonTitleAlignmentSelector';
import { useSectionBlocks } from '../../SectionBlocksContext';
import { deepCopy } from '../../../../utils/object';

export const TextBlock: BlockComponent = () => {
  const { t: tCommon } = useTranslation('common');
  const { t: tComp } = useTranslation('components');
  const { blockPath, blockIndex } = useCurrentBlock();
  const eventManager = useEditorEventManager();
  const sectionBlocks = useSectionBlocks();
  const { getValues } = useFormContext<BlockSpecificPage<TextBlockModel>>();

  const {
    field: { value: bodyText, onChange: onChangeBodyText },
  } = useController<BlockSpecificPage<TextBlockModel>, `${typeof blockPath}.bodytext`>({
    name: `${blockPath}.bodytext`,
  });

  const mediaObject = useWatch<BlockSpecificPage<BannerBlock>, `${BlockPath}.mediaobject`>({
    name: `${blockPath}.mediaobject`,
  });

  const {
    fields: linkItems,
    append,
    update,
    move,
    remove,
  } = useControlledFieldArray<BlockSpecificPage<TextBlockModel>, `${BlockPath}.items`>({
    name: `${blockPath}.items`,
  });

  // The text after the cursor goes to a new text block right below, with the same look. Title, image, links and
  // anchor stay with this block.
  const handleSplit = (before: string, after: string) => {
    const newBlock = deepCopy(getValues(blockPath));
    if (!newBlock || !sectionBlocks) return;
    delete (newBlock as { id?: string }).id;
    onChangeBodyText(before);
    sectionBlocks.insertBlockAfter(blockIndex, {
      ...newBlock,
      bodytext: after,
      title: '',
      visibletitle: false,
      itemsTitle: '',
      items: [],
      listchildren: false,
      mediaobject: null,
      tocId: '',
      icon: undefined,
      _recentlyPasted: true,
    } as TextBlockModel);
  };

  return (
    <Container gap={2} column top left fullWidth>
      <AddMediaObject mediaObject={mediaObject} canEdit>
        <FormControl fullWidth sx={{ mt: 1 }}>
          {mediaObject?.type === 'audio' || mediaObject?.type === 'video' ? (
            <Container
              fullWidth
              left
              top
              mb={2}
              p={2}
              sx={{
                backgroundColor: Styles.Colors.LIGHTEST_BLUE,
                p: 2,
              }}>
              <Typography>{tComp('MediaBlock.AudioVideoContentMessage')}</Typography>
            </Container>
          ) : null}
          <TextInput
            path={`${blockPath}.mediaobject.local.caption`}
            label={tComp('TextBlock.localCaption')}
            size='small'
            multiline
            minRows={2}
            maxRows={4}
            defaultValue=''
          />
          <FormLabel sx={{ fontSize: 'small' }}>{tComp('TextBlock.localCaptionDescription')}</FormLabel>
        </FormControl>
        <Container fullWidth left gap={2} mb={1} mt={1}>
          <SwitchInput<BlockSpecificPage<TextBlockModel>>
            path={`${blockPath}.mediaobject.local.disableZoom`}
            label={tComp('M24Content.HideZoomButton')}
          />
          <SwitchInput<BlockSpecificPage<TextBlockModel>>
            path={`${blockPath}.mediaobject.local.disableCaption`}
            label={tComp('CaptionInput.HideCaption')}
          />
          {mediaObject?.id && (
            <SetAsMainImageCheckbox image={mediaObject as BannerBackgroundM24MediaModel | undefined} />
          )}
        </Container>
      </AddMediaObject>
      <Container spaceBetween fullWidth>
        <TextInput
          fullWidth
          path={`${blockPath}.title`}
          label={tCommon('title')}
          defaultValue=''
          InputProps={{
            endAdornment: <TitleAlignmentSelector blockPath={blockPath} />,
          }}
        />
        <Container sx={{ width: '25%' }}>
          <SwitchInput path={`${blockPath}.visibletitle`} label={tCommon('showTitle')} />
        </Container>
      </Container>

      <Container fullWidth column>
        <WysiwygEditor
          type='tinymce'
          value={bodyText ?? ''}
          onChange={onChangeBodyText}
          blockPath={blockPath}
          onSplit={sectionBlocks ? handleSplit : undefined}
        />
      </Container>

      <Container fullWidth column sx={{ border: '1px solid #ccc' }} p={2}>
        <TextInput path={`${blockPath}.itemsTitle`} label={tComp('TextBlock.TitleOverLinkList')} defaultValue='' />
        <Links
          linkTypes={[LinkType.PAGE, LinkType.EVENT, LinkType.DOCUMENT, LinkType.LINK]}
          onUpdate={update}
          onAppend={append}
          onRemove={remove}
          onReorder={move}
          linkItems={linkItems}
          showListChildrenInput
          blockPath={blockPath}
          useEventHandler={useEditorEventHandler}
          useEventManager={eventManager}
        />
      </Container>

      <TextInput<Page>
        path={`${blockPath}.tocId`}
        label={tComp('CommonBlockContent.FormLabels.AnchorID')}
        defaultValue=''
      />

      <Container left fullWidth>
        <IconFinderButtonSmall path={blockPath} />
      </Container>
      <Typography variant='caption'>{tComp('TextBlock.IconHelpText')}</Typography>
    </Container>
  );
};

export default TextBlock;
